import type { Identifier } from "ra-core";
import { useNotify, useTranslate, useUpdate } from "ra-core";

import { taskHref } from "../../tasks/taskKind";
import type {
  AttentionGroupKey,
  AttentionItem,
  HomeCounters,
} from "./homeModel";

/** Non-component UI helpers shared by the home widgets. */

export const groupAnchor = (group: AttentionGroupKey) => `home-group-${group}`;

/** Scrolls the inbox group into view and moves focus to its heading. */
export const jumpToGroup = (group: AttentionGroupKey) => {
  const target = document.getElementById(groupAnchor(group));
  if (!target) return;
  const reduce = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  target.scrollIntoView({
    behavior: reduce ? "auto" : "smooth",
    block: "start",
  });
  target.focus({ preventScroll: true });
};

/** "2 eventos · 3 tareas · 1 hito" from the today breakdown. */
export const useTodayBreakdownLabel = () => {
  const translate = useTranslate();
  return (breakdown: HomeCounters["todayBreakdown"]) =>
    (
      [
        ["events", breakdown.events],
        ["tasks", breakdown.tasks],
        ["milestones", breakdown.milestones],
      ] as const
    )
      .filter(([, count]) => count > 0)
      .map(([key, count]) =>
        translate(`desk.home.counters.parts.${key}`, { smart_count: count }),
      )
      .join(" · ");
};

/** Deal page: /deals/:id/show on both layouts, so the mobile Oportunidades
 * tab lights up (/informes/:id has no tab). `mobile` kept for callers. */
export const dealHref = (id: Identifier, _mobile: boolean): string =>
  `/deals/${id}/show`;

/** Where an inbox row leads (the record it is about). */
export const attentionHref = (item: AttentionItem, mobile: boolean): string => {
  switch (item.source) {
    case "task":
      return taskHref({ contact_id: item.contactId });
    case "team_task":
      return `/team_tasks/${item.id}`;
    case "milestone":
      return item.dealId != null ? dealHref(item.dealId, mobile) : "/agenda";
    case "event":
      return item.dealId != null ? dealHref(item.dealId, mobile) : "/agenda";
    case "deal":
      return dealHref(item.id, mobile);
    case "invoice":
      return `/invoices/${item.id}`;
  }
};

const DONE_MUTATIONS: Partial<
  Record<
    AttentionItem["source"],
    (now: string) => { resource: string; data: Record<string, unknown> }
  >
> = {
  task: (now) => ({ resource: "tasks", data: { done_date: now } }),
  team_task: (now) => ({
    resource: "team_tasks",
    data: { status: "done", done_at: now },
  }),
  milestone: () => ({ resource: "deal_milestones", data: { status: "done" } }),
};

/** Tasks, team tasks and milestones can be closed right from the inbox. */
export const canMarkDone = (item: AttentionItem): boolean =>
  DONE_MUTATIONS[item.source] != null;

/**
 * «Hecho»: closes the item with the regular data provider update (the
 * cached lists get the new record, so the row leaves the inbox).
 */
export const useMarkDone = () => {
  const [update] = useUpdate();
  const notify = useNotify();
  return (item: AttentionItem) => {
    const mutation = DONE_MUTATIONS[item.source]?.(new Date().toISOString());
    if (!mutation) return;
    update(
      mutation.resource,
      { id: item.id, data: mutation.data, previousData: item.record },
      {
        onSuccess: () =>
          notify("desk.home.inbox.done_notice", {
            type: "success",
            messageArgs: { title: item.title },
          }),
        onError: () => notify("desk.home.inbox.done_error", { type: "error" }),
      },
    );
  };
};
